/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import _ from "lodash"
import moment from "moment"
import numeral from "numeral"
import {
  ActivityIndicator,
  Appearance, TouchableOpacity,
  useWindowDimensions,
  ViewStyle,
  ImageStyle
} from "react-native"
import {
  Cell,
  FloatButton,
} from "../cycle-water-quality-multi-measurement-list/cycle-water-quality-multi-measurement-list-screen"
import {
  Button,
  FlatList,
  JalaIcon,
  JalaLoading,
  LazyRender,
  ListEmpty,
  ListRenderItem,
  ListRenderItemInfo,
  Screen,
  Text,
  AutoImage
} from "../../../../components"
import { Icon, Modal, ScrollView, Skeleton, useDisclose, View } from "native-base"
import { SafeAreaView } from "react-native-safe-area-context"
import { color, nativeBaseTheme, images } from "../../../../theme"
import { navigate } from "../../../../navigators"
import { observer } from "mobx-react-lite"
import { translate } from "../../../../i18n"
import { useStores } from "../../../../models"
import { delay } from "../../../../utils/delay"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const CONTENT_CONTAINER: ViewStyle = {
  // flexGrow: 1,
  paddingBottom: 68
}

const CLOSE_CYCLE_BUTTON: ViewStyle = {
  flex: 1,
  borderRadius: 60,
  backgroundColor: color.palette.white
}

const SCROLL_VIEW_DETAIL_ITEM: ViewStyle = {
  flex: 1,
  marginBottom: 8
}

const BUTTON_DETAIL_ITEM: ViewStyle = {
  width: 120,
  alignSelf: "center",
  borderRadius: 40
}

const EMPTY_NOTES: ImageStyle = {
  resizeMode: 'contain',
  width: 14,
  height: 12,
  alignSelf: 'center'
}

const TableHead: FC<{ colorScheme?: "light" | "dark" }> = observer(function TableHead(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;

  const renderPlaceholder = useCallback(() => {
    return <Skeleton.Text lines={1} paddingX="8px" paddingY="10px" />
  }, [])

  return <View
    borderWidth="1px"
    borderColor={`gray.${colorScheme === 'light' ? '200' : '800'}`}
    backgroundColor={`gray.${colorScheme === 'light' ? '100' : '900'}`}
    flexDirection="row"
  >
    <LazyRender renderPlaceholder={renderPlaceholder}>
      <Cell text={`${_.upperFirst(translate('harvests.harvested_at'))} (DOC)`} width="104px" textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('harvests.weight_short'))} width="72px" textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('harvests.size'))} textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('harvests.status'))} width="64px" textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('harvests.remark'))} width="64px" textPreset="body3" colorScheme={colorScheme} />
      <Cell text={_.upperFirst(translate('edit'))} width="48px" padding="0px" alignItems="center" justifyContent="center" textPreset="body3" colorScheme={colorScheme} />
    </LazyRender>
  </View>;
});

const DetailItemModal: FC<{ colorScheme?: "light" | "dark", item: any }> = observer(function DetailItemModal(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), item = {}, children } = props;
  const { isOpen, onClose, onOpen } = useDisclose()
  const { age, harvested_at, weight, size, selling_price, status, buyer, remark } = item

  return (<>
    <TouchableOpacity onPress={onOpen}>
      {children}
    </TouchableOpacity>
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        flex={1}
        flexGrow={1}
        alignSelf="stretch"
        marginY="80px"
        marginX="16px"
        padding="16px"
        rounded="8px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <Text tx="feeds.remark" transformText={_.upperFirst} preset="display2" colorScheme={colorScheme} marginBottom="8px" />
        <Text
          text={`${moment(harvested_at).format('DD MMM YYYY')} (${age})`}
          preset="body2"
          colorScheme={colorScheme}
          marginBottom="8px"
        />
        <ScrollView
          contentContainerStyle={SCROLL_VIEW_DETAIL_ITEM}
        >
          <Text text={`${_.upperFirst(translate('harvests.weight'))}: ${numeral(weight).format('0,0.[00]')}`} preset="body2" colorScheme={colorScheme} />
          <Text text={`${_.upperFirst(translate('harvests.size'))}: ${numeral(size).format('0,0.[00]')}`} preset="body2" colorScheme={colorScheme} />
          <Text text={`${_.upperFirst(translate('harvests.status'))}: ${status || '-'}`} preset="body2" colorScheme={colorScheme} />
          <Text text={`${_.upperFirst(translate('harvests.price_per_kg'))}: ${numeral(selling_price/weight).format('0,0.[00]')}`} preset="body2" colorScheme={colorScheme} />
          <Text text={`${_.upperFirst(translate('harvests.total_price'))}: ${numeral(selling_price).format('0,0.[00]')}`} preset="body2" colorScheme={colorScheme} />
          <Text text={`${_.upperFirst(translate('harvests.buyer'))}: ${buyer || '-'}`} preset="body2" colorScheme={colorScheme} />
          <Text text={`${_.upperFirst(translate('harvests.remark'))}: ${remark || '-'}`} preset="body2" colorScheme={colorScheme} />
        </ScrollView>
        <Button
          testID="jala:id/cancel-button"
          tx="common.ok"
          preset="primary"
          colorScheme={colorScheme}
          style={BUTTON_DETAIL_ITEM}
          onPress={onClose}
        />
      </View>
    </Modal>
  </>)
})

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, handleEdit: (item: any) => void }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info,
    handleEdit
  } = props;
  const {
    item,
    item: { harvested_at, age, weight, size, status, _pushing, is_can_be_pushed, remark }
  } = info;

  const iconBaseStyle = nativeBaseTheme.components.Icon.baseStyle({ colorMode: colorScheme });
  const sampledAt = moment(harvested_at, 'YYYY-MM-DD');

  const renderPlaceholder = useCallback(() => {
    return <View>
      <Skeleton.Text lines={1} padding="8px" _line={{ height: '14px' }} />
    </View>
  }, [])

  const onPressEdit = useCallback(() => handleEdit(item), [handleEdit, item])

  const valueFormatted = (value) => (
    value != null && numeral(value).format('0,0.[00]') || '-'
  )

  return <LazyRender renderPlaceholder={renderPlaceholder}>
    <View flexDirection="row" position="relative">
      <Cell text={`${sampledAt.format('DD MMM')} (${age})`} width="104px" colorScheme={colorScheme} />
      <Cell text={valueFormatted(weight)} width="72px" colorScheme={colorScheme} textColor="primary.700" />
      <Cell text={valueFormatted(size)} colorScheme={colorScheme} textColor="primary.700" />
      <Cell text={status} width="64px" colorScheme={colorScheme} textColor="primary.700" />
      <Cell width="64px" paddingY="0px" justifyContent="center" colorScheme={colorScheme} alignItems="center">
        {remark ? <DetailItemModal colorScheme={colorScheme} item={item}>
          <Icon {...iconBaseStyle} as={JalaIcon} name="comment-1" size="18px" color="primary.500" />
        </DetailItemModal> : <AutoImage source={images.empty_notes} style={EMPTY_NOTES} />}
      </Cell>
      <Cell width="48px" paddingY="0px" justifyContent="center" alignItems="center" colorScheme={colorScheme}>
        {_pushing
          && <ActivityIndicator size={15} />
          || <TouchableOpacity onPress={onPressEdit}>
            <View size="32px" justifyContent="center" alignItems="center">
              <Icon as={JalaIcon} name="pencil" size="12px" color="primary.500"/>
            </View>
        </TouchableOpacity>}
      </Cell>
      {is_can_be_pushed && (
        <Cell
          width="10px" right="0px" top="5px" position="absolute" paddingY="0px"
          justifyContent="center" colorScheme={colorScheme} backgroundColor="blue.200"
        >
          <Icon {...iconBaseStyle} as={JalaIcon} name="cloud-upload" size="10px" color="primary.500" />
        </Cell>
       )}
    </View>
  </LazyRender>;
})

export const CycleHarvestListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function HarvestTableScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  // const { someStore, anotherStore } = useStores()
  const {
    appStore,
    appStore: {
      isOffline,
      farmStore: {
        pondStore: {
          cycleStore,
          cycleStore: {
            cycle,
            harvests: models,
            loadingHarvest: loadingModel
          }
        }
      }
    },
    harvestStore: modelStore,
    harvestStore: { pushIdQueue, needRefresh, storing, updating, deleting, modifying },
  } = useStores();

  const [dateTimeFilter, setDateTimeFilter] = useState<{
    date_after?: string,
    date_before?: string,
    time?: string
  }>({})

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: pond - view tabs - harvest');
  }, []))

  const modelFlats = useMemo(() => {
    return models.filter(({ harvested_at }) => (
      (!dateTimeFilter.date_after || dateTimeFilter.date_after <= harvested_at)
      && (!dateTimeFilter.date_before || harvested_at <= dateTimeFilter.date_before)
    )).map((m) => ({
      ...m,
      _pushing: !!pushIdQueue[m.id],
      _loading: loadingModel
    }))
  }, [dateTimeFilter, loadingModel, models, pushIdQueue])

  useEffect(() => {
    modelStore.init();
    cycleStore.getHarvests(!isOffline).catch();
  }, [cycleStore, isOffline, modelStore])

  useEffect(() => {
    if (needRefresh) {
      if (isOffline) {
        cycleStore.getHarvests(false).then(() => {
          modelStore.set('needRefresh', null)
          modelStore.set('pushIdQueue', {})
        })
      } else {
        cycleStore.getHarvests(false).then(() => {
          cycleStore.getHarvests(true, () => {
            modelStore.set('needRefresh', null)
            modelStore.set('pushIdQueue', {})
          }).catch()
        })
      }
    }
  }, [cycleStore, isOffline, modelStore, needRefresh])

  const onAddPress = useCallback(async () => {
    JalaAnalytic.log('Mobile app: pond - add harvest');
    await delay(1);
    cycle && navigate('Home.Stack', 'Home.Harvest.Create', { cycle_id: cycle.id });
  }, [cycle])

  const handleEdit = useCallback((item) => {
    navigate('Home.Stack', 'Home.Harvest.Edit', { ...item })
  }, [])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} handleEdit={handleEdit} />
  }, [colorScheme, handleEdit])

  const keyExtractor = useCallback((item) => `${item.id}`, [])

  const onRefresh = useCallback(() => {
    appStore.checkNetworkConnection().then(() => {
      cycleStore.getHarvests(!isOffline).catch()
    }).catch()
  }, [appStore, cycleStore, isOffline])

  const onPressCloseCycle = useCallback(async () => {
    JalaAnalytic.log('Mobile app: pond - close cycle - button clicked');
    await delay(1);
    cycle && navigate('Home.Stack', 'Home.Cycle.Close', { ...cycle })
  }, [cycle])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      {!!modelFlats.length && <TableHead colorScheme={colorScheme} />}
      <FlatList
        data={modelFlats}
        renderItem={renderItem}
        contentContainerStyle={CONTENT_CONTAINER}
        keyExtractor={keyExtractor}
        ListEmptyComponent={(loadingModel || modifying) && (
          <View flexGrow={1} alignItems="center" justifyContent="center" height={`${height - 214}px`}>
            <JalaLoading />
          </View>
        ) || <ListEmpty text={translate("listEmpty.text")} colorScheme={colorScheme} height={`${height - 214}px`} />}
        ListHeaderComponent={(loadingModel || storing) && !!modelFlats.length && !updating && !deleting && (
          <View height="40px" alignItems="center" justifyContent="center" paddingTop="8px">
            <Text tx="fetchingData" preset="body2" colorScheme={colorScheme} color={nativeBaseTheme.colors.muted[colorScheme === 'light' ? '700' : '300']} />
          </View>
        ) || undefined}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={false}
        estimatedItemSize={46}
      />
      {!cycle.finished_at && <FloatButton
        colorScheme={colorScheme}
        dateTimeFilterValue={dateTimeFilter}
        onDateTimeFilterChange={setDateTimeFilter}
        onAddPress={onAddPress}
        prepend={<View
          flex={1}
          backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
          flexDirection="row" marginX="12px" marginBottom="12px" borderRadius="60px" shadow="4" zIndex={6}
        >
          <Button
            testID="jala:id/close-cycle-button"
            tx="cycleHarvestListScreen.closeCycle"
            preset="outline"
            textPreset="title3"
            colorScheme={colorScheme}
            textColor={colorScheme === "light" ? color.primary : undefined}
            style={CLOSE_CYCLE_BUTTON}
            onPress={onPressCloseCycle}
          />
        </View>}
      />}
    </Screen>
  )
})
